import {
  CHECKOUT_EMAIL_UPDATE_ERROR,
  CHECKOUT_EMAIL_UPDATE_SUCCESS,
  IS_UPDATING_BILLING_INFORMATION,
  IS_UPDATING_CONTACT_EMAIL,
  IS_UPDATING_SHIPPING_INFORMATION,
  SET_SHIPPING_METHOD,
  UPDATING_BILLING_INFORMATION_ERROR,
  UPDATING_BILLING_INFORMATION_SUCCESS,
  UPDATING_CONTACT_EMAIL_ERROR,
  UPDATING_CONTACT_EMAIL_SUCCESS,
  UPDATING_SHIPPING_INFORMATION_ERROR,
  UPDATING_SHIPPING_INFORMATION_SUCCESS,
} from "../../constants/ActionTypes";
import axios from "../../utils/api";
import { setGuest } from "./Auth";
import { getShippingRates, updateCheckout, updateCheckoutWithShippingRate } from "./Checkout";
import {
  formatAddressToShopifyFormat,
  getLocalStorage,
  getShippingCookieName,
  hasAlreadySetShippingAddress,
  normalizeAddressInformation,
  setLocalStorage,
} from "../../utils/helpers";
import { SAME_AS_BILLING_ADDRESS } from "../../constants/AppConstants";

/**
 * Update Billing Information
 * call the api to update billing information
 * on success update the checkout email (call shopify api to update the checkout contact email)
 *
 * if user is Guest we just update local state,
 * also we update the checkout contact email (call shopify api to update the checkout contact email
 *
 * @returns {(function(*): void)|*}
 * @constructor
 */
export const updateBillingInformation = (data, callback) => {
  return async (dispatch, getState) => {
    dispatch({
      type: IS_UPDATING_BILLING_INFORMATION,
      payload: true,
    });

    // If our user is a guest we just update the local state
    // and update the checkout contact email
    if (getState().auth?.guest) {
      dispatch({
        type: UPDATING_BILLING_INFORMATION_SUCCESS,
        payload: data,
      });
      dispatch(setShippingInformation(data));
      dispatch(updateCheckoutEmail(data?.email));
      callback();
      return;
    }

    axios
      .put(`/api/me/profile/update/`, data)
      .then(({ data }) => {
        dispatch(setGuest(false));
        // Set the checkout Contact email
        dispatch(setShippingInformation(data));
        dispatch(updateCheckoutEmail(data?.email));
        dispatch({
          type: UPDATING_BILLING_INFORMATION_SUCCESS,
          payload: data,
        });
        callback();
      })
      .catch(error => {
        dispatch({
          type: UPDATING_BILLING_INFORMATION_ERROR,
          payload: error.message,
        });
      });
  };
};

const getUpdatedShippingInfo = (getState, shippingCookieName, data, updatedUsingForm) =>
  hasAlreadySetShippingAddress() && !updatedUsingForm
    ? JSON.parse(getLocalStorage(shippingCookieName))
    : data;

/**
 * Set Shipping Information
 * @param data
 * @param callback
 * @param updatedUsingForm
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const setShippingInformation = (data, callback = () => {}, updatedUsingForm = false) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: IS_UPDATING_SHIPPING_INFORMATION });
      const currentLanguage = getState().common.currentLanguage;
      const shippingCookieName = getShippingCookieName(currentLanguage);
      const shippingInfoData = getUpdatedShippingInfo(
        getState,
        shippingCookieName,
        data,
        updatedUsingForm
      );
      const updatedShippingInfo = normalizeAddressInformation(shippingInfoData);
      const shippingInfo = formatAddressToShopifyFormat(updatedShippingInfo);
      const checkoutId = getState().checkout?.checkoutId;
      const checkout = await getState().shopify?.client?.checkout?.updateShippingAddress(
        checkoutId,
        shippingInfo
      );
      setLocalStorage(shippingCookieName, JSON.stringify(updatedShippingInfo));
      if (checkout?.userErrors?.length > 0) {
        const firstError = checkout.userErrors[0].message;
        dispatch({ type: UPDATING_SHIPPING_INFORMATION_ERROR, payload: firstError });
        return;
      }
      dispatch({ type: UPDATING_SHIPPING_INFORMATION_SUCCESS, payload: updatedShippingInfo });
      await dispatch(updateCheckout(checkout));
      callback();

      await dispatch(getShippingRates());

      if (updatedUsingForm) {
        window.location.href = checkout?.webUrl;
      }
    } catch (error) {
      console.error("Error updating shipping information", error);
      dispatch({ type: UPDATING_SHIPPING_INFORMATION_ERROR, payload: error.message });
    }
  };
};

/**
 * Set Shipping Method
 * @param data
 * @returns {(function(*): void)|*}
 */
export const setShippingMethod = data => {
  return dispatch => {
    dispatch(updateCheckoutWithShippingRate(data));
    dispatch({
      type: SET_SHIPPING_METHOD,
      payload: data,
    });
  };
};

/**
 * Set Shipping Address Same As Billing
 */
export const setShippingAddressSameAsBilling = () => {
  return (dispatch, useState) => {
    const { billingInformation } = useState().user;
    dispatch(setShippingInformation(billingInformation));
    dispatch({
      type: SAME_AS_BILLING_ADDRESS,
      payload: true,
    });
  };
};

/**
 * Update checkout email
 * @param email
 * @returns {(function(*, *): Promise<void>)|*}
 */
export const updateCheckoutEmail = email => {
  return async (dispatch, getState) => {
    const client = getState().shopify.client;
    const checkoutId = getState().checkout?.checkout?.id;
    client.checkout
      .updateEmail(checkoutId, email)
      .then(checkout => {
        dispatch({
          type: CHECKOUT_EMAIL_UPDATE_SUCCESS,
          payload: checkout,
        });
      })
      .catch(error => {
        dispatch({
          type: CHECKOUT_EMAIL_UPDATE_ERROR,
          payload: error?.message,
        });
      });
  };
};

export const updateContactEmail = (email, callback = () => {}) => {
  return async (dispatch, getState) => {
    const client = getState().shopify.client;
    const checkoutId = getState().checkout?.checkout?.id;
    dispatch({
      type: IS_UPDATING_CONTACT_EMAIL,
    });
    client.checkout
      .updateEmail(checkoutId, email)
      .then(checkout => {
        dispatch(updateCheckout(checkout));
        dispatch({
          type: UPDATING_CONTACT_EMAIL_SUCCESS,
          payload: email,
        });
        callback();
      })
      .catch(error => {
        dispatch({
          type: UPDATING_CONTACT_EMAIL_ERROR,
          payload: error?.message,
        });
      });
  };
};
